import type { GridApi, IRowNode } from 'ag-grid-community';
import { getAgGridKey } from '@/utilities/i18n.utilities';
import { ColumnToDerivedColumn, VantageNames } from '@/vantage/constants';
import { t } from 'i18next';
import _ from 'lodash';

/**
 * Retrieves the translated column header name for a given column name.
 * If a translation is found, it returns the translated name; otherwise, it returns the original column name.
 *
 * @param columnName - The name of the column to retrieve the header name for.
 *
 * @returns The translated column header name or the original/derived column name if no translation is found.
 */
export const getColumnHeaderName = (columnName: string) => {
  const name = ColumnToDerivedColumn[columnName] ?? columnName;
  const key = `VANTAGE.EVIDENCE_TABLE.COLUMNS.${getAgGridKey(name)}`;
  return t([key, name]);
};

/**
 * Applies column visibility settings to an AG Grid instance.
 *
 * Sets columns to visible or hidden based on a provided list of columns.
 * All columns not included in the visibleColumns array will be hidden.
 *
 * @param api - The AG Grid API instance used to manage column state
 * @param visibleColumns - Array of column IDs that should be visible
 *
 * @returns void
 *
 * @example
 * ```typescript
 * const visibleColumns = ['name', 'email', 'status'];
 * applyColumnVisibility(gridApi, visibleColumns);
 * ```
 *
 * @remarks
 * - Function will return early if api is null/undefined
 * - Preserves all existing column state properties except for the 'hide' property
 */
export const applyColumnVisibility = (api: GridApi, visibleColumns: string[]): void => {
  if (!api) return;

  const allColumnState = api.getColumnState();
  const newColumnState = allColumnState.map((col) => {
    const shouldBeVisible = visibleColumns.includes(col.colId);
    return {
      ...col,
      hide: !shouldBeVisible,
    };
  });

  api.applyColumnState({ state: newColumnState });
};

export const hasMultiModalFilter = (columnName: string) =>
  [VantageNames.Columns.Flagged, VantageNames.Columns.Labels, VantageNames.Columns.Suppressed].includes(columnName);

/**
 * Safely get a node's value for the given column:
 * - If group node and useAggData=true -> read node.aggData[colId] (may be null/undefined/[]/"")
 * - Else -> node.getValue(colKey) which respects field/valueGetter
 */
export function makeNodeValueGetter(api: GridApi, field: string) {
  const column = api.getColumn(field);
  const aggKey = column?.getColId?.() ?? field;

  return (node: IRowNode, useAggData = true) => {
    // Groups: read aggregator output by colId
    if (useAggData && (node as any).group) {
      const agg = (node as any).aggData;
      if (agg && Object.prototype.hasOwnProperty.call(agg, aggKey)) {
        return agg[aggKey]; // may be null/undefined/[]/""
      }
      return undefined; // treat missing agg entry as blank
    }

    // Leaves: run the column's valueGetter with a *real* getValue
    if (!column) return (node as any).data?.[field];

    const colDef = column.getColDef();

    if (_.isFunction(colDef.valueGetter)) {
      // Cast to any. Alternatively could use generics, but that adds complexity for little gain
      const apiAny = api as any;
      const apiGetValue = _.isFunction(apiAny.getValue) ? apiAny.getValue.bind(apiAny) : undefined;

      const safeGetValue = (colKey: string) => {
        try {
          return apiGetValue ? apiGetValue(colKey, node) : (node as any).data?.[colKey];
        } catch {
          return (node as any).data?.[colKey];
        }
      };

      try {
        return colDef.valueGetter({
          data: (node as any).data,
          node,
          colDef: colDef as any,
          column: column as any,
          api: api as any,
          context: (apiAny.getContext?.() ?? {}) as any,
          getValue: safeGetValue,
        } as any);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('valueGetter error', e);
        }
      }
    }

    return (node as any).data?.[field];
  };
}
